import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ListingFacades } from '@components/listing-facades/models';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterToggleGroupComponent } from '@shared/components/filter-toggle-group';
import { GridSkeletonComponent } from '@shared/components/grid-skeleton';
import { Image } from '@shared/components/image/models';
import { OptionCardComponent } from '@shared/components/option-card';
import { PopinImageComponent } from '@shared/components/popin-image';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { LabelKey } from '@shared/models';
import { DialogService } from '@shared/services/dialog';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { Front } from '@shared/services/elastic-search/models';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

type Indice = { current: number; length: number };

@Component({
  selector: 'app-listing-facades',
  imports: [
    FilterToggleGroupComponent,
    GridSkeletonComponent,
    OptionCardComponent,
    SvgComponent,
    TitleComponent,
    TranslatePipe,
  ],
  templateUrl: './listing-facades.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListingFacadesComponent extends AbstractDynamicComponent<ListingFacades> implements OnInit {
  protected readonly BREATHING_TEXT_INDEX = 6;
  protected readonly filters = signal<LabelKey[]>(null!);
  protected readonly fronts = signal<Front[] | null>(null);
  protected readonly frontsImagesCurrentIndices = signal<Indice[]>([]);
  protected readonly filterName = 'filter_front_category';
  private readonly filtersUrlParam = inject(UrlParamsService).param(this.filterName);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly dialogService = inject(DialogService);
  private readonly logs = inject(LogsService);

  constructor() {
    super();

    effect(() => {
      // Required filter: don't fetch until the filter is in the URL, so we never run an
      // unfiltered query that would be immediately replaced once the default filter is applied.
      const selectedFilter = this.filtersUrlParam();
      if (!selectedFilter) return;
      const categoryName = this.data.categoryName;
      this.elasticSearchService
        .getFronts(categoryName, selectedFilter.split('__'))
        .pipe(
          take(1),
          tap((fronts) => {
            this.buildFrontsImagesCurrentIndices(fronts);
          })
        )
        .subscribe({ error: (err) => this.logs.error('ListingFacadesComponent', 'getFronts failed', err) });
    });
  }

  ngOnInit(): void {
    this.elasticSearchService
      .getFrontsFilter()
      .pipe(
        take(1),
        tap((filters) => this.filters.set(filters))
      )
      .subscribe({ error: (err) => this.logs.error('ListingFacadesComponent', 'getFrontsFilter failed', err) });
  }

  updateImageIndex(frontIndex: number, step: -1 | 1): void {
    this.frontsImagesCurrentIndices.update((indices) => {
      const updated = [...indices];

      const currentIndex = updated[frontIndex]!.current;
      const length = updated[frontIndex]!.length;

      updated[frontIndex]!.current = (currentIndex + step + length) % length;

      return updated;
    });
  }

  openVisualFullScreenModal(image: Image): void {
    this.dialogService.openDialog(PopinImageComponent, 'FULL_SCREEN', {
      data: {
        image,
      },
    });
  }

  private buildFrontsImagesCurrentIndices(fronts: Front[]): void {
    const imagesCurrentIndices = fronts.reduce((acc, front) => {
      acc.push({ current: 0, length: front.images.length });
      return acc;
    }, [] as Indice[]);
    this.frontsImagesCurrentIndices.set(imagesCurrentIndices);
    this.fronts.set(fronts);
  }
}
